
import { useState, useEffect } from 'react';
import { ClientSubscription } from '@/models/Client';
import { useSubscriptionPayments } from './useSubscriptionPayments';
import { calculateSubscriptionPaymentInfo } from '@/utils/subscriptionPayments';

interface UsePaymentFormProps {
  open: boolean;
  subscription?: ClientSubscription;
  activeSubscriptions: ClientSubscription[];
  clientId: string;
}

export const usePaymentForm = ({ open, subscription, activeSubscriptions, clientId }: UsePaymentFormProps) => {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  
  // Get subscription payments for the selected subscription
  const { data: subscriptionPayments = [] } = useSubscriptionPayments(clientId, selectedSubscriptionId);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      
      // Pre-select subscription if provided, otherwise clear selection
      if (subscription) {
        setSelectedSubscriptionId(subscription.id);
      } else {
        setSelectedSubscriptionId('');
      }
    }
  }, [open, subscription]);

  // Auto-fill amount when a subscription is selected (but allow user to change it)
  useEffect(() => {
    if (selectedSubscriptionId && activeSubscriptions.length > 0) {
      const selectedSubscription = activeSubscriptions.find(sub => sub.id === selectedSubscriptionId);
      if (selectedSubscription) {
        const { remainingAmount, isPartial } = calculateSubscriptionPaymentInfo(selectedSubscription, subscriptionPayments);
        
        // For partial subscriptions, prefill the remaining amount
        // For other subscriptions, prefill the full price
        const prefillAmount = isPartial ? remainingAmount : selectedSubscription.price;
        
        // Only set the amount if it's currently empty to avoid overwriting user changes
        if (paymentAmount === '' || paymentAmount === '0') {
          setPaymentAmount(prefillAmount.toString());
        }
      }
    }
  }, [selectedSubscriptionId, activeSubscriptions, subscriptionPayments]);

  return {
    paymentAmount,
    setPaymentAmount,
    paymentDate,
    setPaymentDate,
    notes,
    setNotes,
    selectedSubscriptionId,
    setSelectedSubscriptionId,
  };
};
